import React, { useState, useEffect } from 'react';

interface EditMealModalProps {
  meal: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedMeal: any) => void;
  loading: boolean;
}

export function EditMealModal({ meal, isOpen, onClose, onSave, loading }: EditMealModalProps) {
  const [formData, setFormData] = useState({
    input_text: '',
    protein_g: 0,
    carbs_g: 0,
    fat_g: 0,
    calories: 0,
    meal_time: ''
  });

  useEffect(() => {
    if (meal) {
      setFormData({
        input_text: meal.input_text || '',
        protein_g: meal.protein_g || 0,
        carbs_g: meal.carbs_g || 0,
        fat_g: meal.fat_g || 0,
        calories: meal.calories || 0,
        meal_time: meal.meal_time ? new Date(meal.meal_time).toISOString().slice(0, 16) : ''
      });
    }
  }, [meal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="retro-card p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-text-dark">
            Edit Meal
          </h3>
          <button
            onClick={onClose}
            className="text-text-medium hover:text-text-dark"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label 
              htmlFor="input_text" 
              className="block text-sm font-medium text-text-medium mb-1"
            >
              Meal Description
            </label>
            <input
              id="input_text"
              type="text"
              value={formData.input_text}
              onChange={(e) => handleInputChange('input_text', e.target.value)}
              className="vintage-input w-full"
              required
              placeholder="Enter meal description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label 
                htmlFor="protein_g" 
                className="block text-sm font-medium text-text-medium mb-1"
              >
                Protein (g)
              </label>
              <input
                id="protein_g"
                type="number"
                value={formData.protein_g}
                onChange={(e) => handleInputChange('protein_g', parseFloat(e.target.value) || 0)}
                className="vintage-input w-full"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label 
                htmlFor="carbs_g" 
                className="block text-sm font-medium text-text-medium mb-1"
              >
                Carbs (g)
              </label>
              <input
                id="carbs_g"
                type="number"
                value={formData.carbs_g}
                onChange={(e) => handleInputChange('carbs_g', parseFloat(e.target.value) || 0)}
                className="vintage-input w-full"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label 
                htmlFor="fat_g" 
                className="block text-sm font-medium text-text-medium mb-1"
              >
                Fat (g)
              </label>
              <input
                id="fat_g"
                type="number"
                value={formData.fat_g}
                onChange={(e) => handleInputChange('fat_g', parseFloat(e.target.value) || 0)}
                className="vintage-input w-full"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label 
                htmlFor="calories" 
                className="block text-sm font-medium text-text-medium mb-1"
              >
                Calories {formData.calories < 0 && <span className="text-xs text-purple-600">(Workout)</span>}
              </label>
              <input
                id="calories"
                type="number"
                value={formData.calories}
                onChange={(e) => handleInputChange('calories', parseInt(e.target.value) || 0)}
                className="vintage-input w-full"
                placeholder="Negative for workouts"
              />
            </div>
          </div>

          <div>
            <label 
              htmlFor="meal_time" 
              className="block text-sm font-medium text-text-medium mb-1"
            >
              Meal Time
            </label>
            <input
              id="meal_time"
              type="datetime-local"
              value={formData.meal_time}
              onChange={(e) => handleInputChange('meal_time', e.target.value)}
              className="vintage-input w-full"
              required
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-text-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 neon-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 